import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { AuthContext, AuthContextType } from "@/context/AuthContext";
import { useApiErrorHandler } from "@/lib/errorHandler";
import { Case } from "@/api/cases";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import {
  reviewApi,
  ReviewCaseAddEditCustomTags,
  ReviewCaseAddEditDefaultTags,
  ReviewCaseAddEditTags,
} from "@/api/review";
import { Checkbox } from "@/components/ui/checkbox";
import { useTranslation } from "react-i18next";

function AddEditTags({
  trigger,
  reviewCase,
  refresh,
}: {
  reviewCase: Case;
  trigger: JSX.Element;
  refresh: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [caseTags, setTags] = useState<ReviewCaseAddEditTags>();
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: boolean;
  }>({});
  const { values: contextValues, set } = useContext(
    AuthContext,
  ) as AuthContextType;
  const errorHandler = useApiErrorHandler("AddEditTags", {});
  const { loading, user } = contextValues;
  const { setLoading } = set;

  async function fetchCaseTags() {
    try {
      const result = await reviewApi.getCaseTags({
        id: reviewCase.id,
        token: user?.access,
      });
      setTags(result.data);
    } catch (error) {
      errorHandler(error);
    }
  }

  useEffect(() => {
    fetchCaseTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewCase.id]);

  const validateTags = useCallback(() => {
    const errors: { [key: string]: boolean } = {};
    let hasErrors = false;

    [
      ...(caseTags?.default_tags || []),
      ...(caseTags?.custom_tags || []),
    ].forEach((tag) => {
      if (tag.checked && !tag.name?.trim()) {
        const tagType = "placeholder" in tag ? "custom" : "default";
        errors[`${tag.id}-${tagType}`] = true;
        hasErrors = true;
      }
    });

    setValidationErrors(errors);
    return !hasErrors;
  }, [caseTags]);

  const isValid = useMemo(() => validateTags(), [caseTags]); // eslint-disable-line react-hooks/exhaustive-deps

  async function onSubmit() {
    try {
      setLoading?.(true);
      await reviewApi.addEditCaseTags({
        id: reviewCase.id,
        token: user?.access,
        data: {
          tags: [
            ...(caseTags?.default_tags || []),
            ...(caseTags?.custom_tags || []),
          ]
            .filter((item) => item.checked)
            .map((item) => ({
              key: item.id.toString(),
              value: item.name,
            })),
        },
      });
      toast({
        title: t("common.toast.updated_successfully", {
          resource: t("common.label.tag"),
        }),
      });
      await refresh();
      setOpen(false);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading?.(false);
    }
  }

  function onCancel() {
    setOpen(false);
  }

  const onCheckboxChange = useCallback(
    (checked: boolean, tagId: number, tagType: "default" | "custom") => {
      if (tagType === "default") {
        let tempTags = Object.assign(
          [],
          caseTags?.default_tags,
        ) as ReviewCaseAddEditDefaultTags[];
        tempTags = tempTags.map((item) => {
          if (item.id === tagId) {
            return {
              ...item,
              checked: checked,
              type: "default",
            };
          } else {
            return item;
          }
        });
        setTags({
          case_id: caseTags?.case_id || 0,
          default_tags: tempTags,
          custom_tags: caseTags?.custom_tags || [],
        });
      } else {
        let tempTags = Object.assign(
          [],
          caseTags?.custom_tags,
        ) as ReviewCaseAddEditCustomTags[];
        tempTags = tempTags.map((item) => {
          if (item.id === tagId) {
            return {
              ...item,
              checked: checked,
              type: "custom",
            };
          } else {
            return item;
          }
        });
        setTags({
          case_id: caseTags?.case_id || 0,
          default_tags: caseTags?.default_tags || [],
          custom_tags: tempTags,
        });
      }
    },
    [caseTags],
  );

  const onTagValueChange = useCallback(
    (
      e: React.ChangeEvent<HTMLInputElement>,
      tagId: number,
      tagType: "default" | "custom",
    ) => {
      if (tagType === "default") {
        let tempTags = Object.assign(
          [],
          caseTags?.default_tags,
        ) as ReviewCaseAddEditDefaultTags[];
        tempTags = tempTags.map((item) => {
          if (item.id === tagId) {
            return {
              ...item,
              name: e.target.value,
            };
          } else {
            return item;
          }
        });
        setTags({
          case_id: caseTags?.case_id || 0,
          default_tags: tempTags,
          custom_tags: caseTags?.custom_tags || [],
        });
      } else {
        let tempTags = Object.assign(
          [],
          caseTags?.custom_tags,
        ) as ReviewCaseAddEditCustomTags[];
        tempTags = tempTags.map((item) => {
          if (item.id === tagId) {
            return {
              ...item,
              name: e.target.value,
            };
          } else {
            return item;
          }
        });
        setTags({
          case_id: caseTags?.case_id || 0,
          default_tags: caseTags?.default_tags || [],
          custom_tags: tempTags,
        });
      }
    },
    [caseTags],
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className="w-full md:w-[500px] max-h-screen overflow-y-auto"
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t("cases.title.add_edit_tags")}</DialogTitle>
          <DialogDescription>
            {t("cases.statictext.add_update_custom_labels_for", {
              name: reviewCase.name,
            })}
          </DialogDescription>
        </DialogHeader>

        <h3 className="text-xl">{t("cases.title.default_tags")}</h3>
        <table>
          {caseTags?.default_tags.map((item) => (
            <tr key={`default-${item.id}`} className="my-4 h-[46px]">
              <td>
                <Checkbox
                  checked={item.checked}
                  onCheckedChange={(checked: boolean) =>
                    onCheckboxChange(checked, item.id, "default")
                  }
                />
              </td>
              <td>
                <img
                  className="w-[32px]"
                  alt={item.name}
                  src={`${process.env.REACT_APP_BACKEND_URL}${item.url}`}
                />
              </td>
              <td className="w-[85%]">
                <div className="flex flex-col">
                  <Input
                    type="text"
                    disabled
                    value={item.name}
                    className={`${validationErrors[`${item.id}-default`] ? "border-red-500" : ""}`}
                    onChange={(e) => onTagValueChange(e, item.id, "default")}
                  />
                  {validationErrors[`${item.id}-default`] && (
                    <span className="text-red-500 text-sm my-1">
                      {t("cases.statictext.tag_name_required")}
                    </span>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </table>
        <br />
        <br />
        <h3 className="text-xl">{t("cases.title.custom_tags")}</h3>
        <table>
          {caseTags?.custom_tags.map((item) => (
            <tr key={`custom-${item.id}`} className="my-4 h-[46px]">
              <td>
                <Checkbox
                  checked={item.checked}
                  onCheckedChange={(checked: boolean) =>
                    onCheckboxChange(checked, item.id, "custom")
                  }
                />
              </td>
              <td>
                <img
                  className="w-[32px]"
                  alt={item.placeholder || item.name}
                  src={`${process.env.REACT_APP_BACKEND_URL}${item.url}`}
                />
              </td>
              <td className="w-[85%]">
                <div className="flex flex-col">
                  <Input
                    type="text"
                    value={item.name}
                    placeholder={item.placeholder}
                    className={`${validationErrors[`${item.id}-custom`] ? "border-red-500" : ""}`}
                    onChange={(e) => onTagValueChange(e, item.id, "custom")}
                  />
                  {validationErrors[`${item.id}-custom`] && (
                    <span className="text-red-500 text-sm my-1">
                      {t("cases.statictext.tag_name_required")}
                    </span>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </table>

        <DialogFooter>
          <Button variant="secondary" disabled={loading} onClick={onCancel}>
            {loading ? t("common.button.loading") : t("common.button.cancel")}
          </Button>
          <Button
            variant="default"
            disabled={loading || !isValid}
            onClick={onSubmit}
          >
            {loading ? t("common.button.loading") : t("common.button.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AddEditTags;
